import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { onApiProblem, tenantApi } from '../api/client'
import type { AuthSession } from '../api/types'

interface AppStateValue {
  authenticated: boolean
  bootstrapping: boolean
  readOnly: boolean
  session: AuthSession | null
  setSession: (session: AuthSession) => void
  clearSession: () => void
}

const AppStateContext = createContext<AppStateValue | null>(null)

let sessionRestoreRequest: Promise<AuthSession | null> | null = null

function restoreSessionSingleFlight() {
  if (!sessionRestoreRequest) {
    sessionRestoreRequest = tenantApi.restoreSession().finally(() => {
      sessionRestoreRequest = null
    })
  }
  return sessionRestoreRequest
}

export function AppStateProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [session, setSessionState] = useState<AuthSession | null>(null)
  const [bootstrapping, setBootstrapping] = useState(true)

  const clearSession = useCallback(() => {
    setSessionState(null)
    queryClient.clear()
  }, [queryClient])

  const setSession = useCallback((next: AuthSession) => {
    if (session?.user.id !== next.user.id || session?.user.tenantId !== next.user.tenantId)
      queryClient.clear()
    setSessionState(next)
  }, [queryClient, session?.user.id, session?.user.tenantId])

  useEffect(() => {
    let active = true
    void restoreSessionSingleFlight()
      .then((restored) => {
        if (active && restored) setSessionState(restored)
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setBootstrapping(false)
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(
    () =>
      onApiProblem((problem) => {
        if (problem.status === 401) clearSession()
        if (problem.status === 403 && problem.code === 'LICENSE_READ_ONLY') {
          setSessionState((current) =>
            current ? { ...current, user: { ...current.user, readOnly: true } } : current,
          )
        }
      }),
    [clearSession],
  )

  const value = useMemo<AppStateValue>(
    () => ({
      authenticated: session !== null,
      bootstrapping,
      readOnly: session?.user.readOnly ?? false,
      session,
      setSession,
      clearSession,
    }),
    [bootstrapping, clearSession, session, setSession],
  )

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
}

export function useAppState() {
  const value = useContext(AppStateContext)
  if (!value) throw new Error('useAppState must be used inside AppStateProvider')
  return value
}
